/* =======================================================================
   Knowledge Base — HTML builders for the content column. Azure item 416.

   Ported from the mockup on 10 Sep 2026. These return markup strings that
   KnowledgeBase.js drops in with dangerouslySetInnerHTML, and every link
   inside them is a data-* attribute the page handles by delegation:

     data-go="<slug>"      open an article ("home" = landing page)
     data-mod-go="<mod>"   open a module
     data-help             "was this helpful"
     data-copy / data-md   the two ghost buttons on an article
     data-flagtoggle       hide / show every open question
     .toclink[data-h]      on-this-page jump
     .verify-h             collapse one open question

   Article bodies are authored as HTML strings in articles.js, so rendering
   them any other way would mean parsing them anyway. Keeping the whole
   column as one string keeps the article text and its chrome in one
   place, and keeps the mockup and the app rendering identically — which
   is what makes the mockup useful as a preview.

   Pure: no DOM, no state. Anything that needs the rendered DOM (the table
   of contents, scroll-spy, search highlighting) lives in the component.
   ======================================================================= */
import { ARTICLES, MODULES, MOD_ICON, byId, strip } from './articles';
import { refOf } from './kbSession';
import { matches, searchTerms, termRe } from './search';

/* our article tags (WO-01 …) — rendered only while refs mode is on */
const REF = a => { const r = refOf(a); return r ? '<span class="kb-ref">' + r + '</span>' : ''; };

const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/* ---------------- landing ---------------- */
export function renderLanding(){
  const featured = [
    ['create-work-order', 'Start a new job from scratch'],
    ['work-order-statuses', 'What each status means, and who moves it'],
    ['assign-technicians', 'Put one or more techs on a job'],
    ['create-po-from-work-order', 'Order parts — and why it starts on the work order']
  ];
  const quickstart = [
    ['find-work-orders', 'Find your way around the work order list', 'Branch tabs, search, and filters'],
    ['create-work-order', 'Create your first work order', 'Pick a branch first — the button is hidden otherwise'],
    ['assign-technicians', 'Send it to a technician', 'Primary tech, plus anyone else on the crew'],
    ['work-order-service-ticket', 'Close it out with a service ticket', 'The printable sheet the customer signs']
  ];

  return '<article class="lp">' +
    '<div class="hero">' +
      '<h1>Makani help</h1>' +
      '<p class="lede">Short, step-by-step answers for getting things done in Makani — written for the people actually running the jobs, not for a manual nobody reads.</p>' +
      '<div class="hero-search" id="herosearch">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>' +
        'Search for what you are trying to do…<span class="hk">/</span></div>' +
    '</div>' +

    '<div class="lp-sec"><h2 class="lp-h">New to Makani? Start here</h2><div class="qs">' +
      quickstart.map(([slug, t, d], i) =>
        '<div class="qs-row" data-go="' + slug + '"><div class="qs-n">' + (i + 1) + '</div>' +
        '<div><div class="qs-t">' + t + '</div><div class="qs-d">' + d + '</div></div>' +
        '<div class="qs-go"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M9 6l6 6-6 6"/></svg></div></div>').join('') +
    '</div></div>' +

    '<div class="lp-sec"><h2 class="lp-h">Most read</h2><div class="feat-grid">' +
      featured.map(([slug, d]) => {
        const a = byId(slug);
        return '<div class="feat" data-go="' + slug + '"><div class="feat-ic">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M14 3v5h5"/><path d="M19 8v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7z"/></svg></div>' +
          '<div><h3>' + a.title + '</h3><p>' + d + '</p></div></div>';
      }).join('') +
    '</div></div>' +

    '<div class="lp-sec"><h2 class="lp-h">Browse by module</h2><div class="feat-grid">' +
      MODULES.map(m => {
        const n = ARTICLES.filter(a => a.module === m && !a.stub).length;
        return '<div class="feat" data-mod-go="' + m + '"><div class="feat-ic">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">' + MOD_ICON[m] + '</svg></div>' +
          '<div><h3>' + m + '</h3><p>' + (n ? n + ' article' + (n > 1 ? 's' : '') : 'Nothing written yet') + '</p></div></div>';
      }).join('') +
    '</div></div>' +

    '<div class="helpfoot"><h3>Can’t find what you need?</h3>' +
      '<p>Search anyway — every search that comes back empty is logged, and those are the articles we write next. ' +
      'If it is urgent, reach out to the Makani team directly.</p></div>' +
    '</article>';
}

/* ---------------- article ---------------- */
function relCard(a){
  if (!a) return '';
  return '<div class="rel-card" data-go="' + a.slug + '"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M14 3v5h5"/><path d="M19 8v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7z"/></svg>' +
    '<div><div class="rel-title">' + a.title + '</div><div class="rel-mod">' + a.module + '</div></div></div>';
}

export function countFlags(a){
  return a && a.body ? (a.body.match(/class="verify"/g) || []).length : 0;
}

export function renderArticle(a, flagsHidden){
  const sibs = ARTICLES.filter(x => x.module === a.module && !x.stub);
  const i = sibs.indexOf(a), prev = sibs[i - 1], next = sibs[i + 1];
  const nv = countFlags(a);

  let h = '<article><div class="crumb"><a data-go="home">Knowledge Base</a><span class="sep">/</span>' +
    '<a data-mod-go="' + a.module + '">' + a.module + '</a><span class="sep">/</span>' +
    '<span class="cur">' + a.title + '</span></div>' +
    '<h1>' + REF(a) + a.title + '</h1>' +
    '<div class="meta"><span class="pill">' + (a.role || 'Admin') + '</span><span class="pill">' + a.module + '</span>' +
    '<span>Updated ' + a.updated + '</span><div class="meta-actions">' +
    '<button class="ghost-btn" data-copy="1" type="button"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>Copy for LLM</button>' +
    '<button class="ghost-btn" data-md="1" type="button"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 3v5h5"/><path d="M19 8v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7z"/></svg>View as Markdown</button>' +
    '</div></div>';

  if (nv){
    h += '<div class="draftbar"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 9v4M12 17h.01"/><path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>' +
      '<p><b>Unverified draft — ' + nv + ' open question' + (nv > 1 ? 's' : '') + '.</b> Drafted from app walks on 24–25 Aug 2026. Button names and steps have not been checked against the source code, and the flagged points below are unresolved. Not for publication.</p>' +
      '<button class="draft-toggle" data-flagtoggle="1" type="button">' + (flagsHidden ? 'Show' : 'Hide') + ' open questions</button></div>';
  }
  h += '<div class="answer"><div class="answer-label"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>Short answer</div><p>' + a.answer + '</p></div>';
  h += a.body;

  if (a.related && a.related.length){
    h += '<h2>Related articles</h2><div class="related">' + a.related.map(s => relCard(byId(s))).join('') + '</div>';
  }
  h += '<div class="helpful" id="helpful"><span class="helpful-q">Was this article helpful?</span>' +
    '<div class="helpful-btns">' +
    '<button class="hb" data-help="1" type="button"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z"/><path d="M7 11l4.5-8a2.2 2.2 0 0 1 3 2.8L13 10h5.6a2 2 0 0 1 2 2.5l-1.8 6.5a2 2 0 0 1-2 1.5H7"/></svg>Yes</button>' +
    '<button class="hb" data-help="1" type="button"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" style="transform:rotate(180deg)"><path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z"/><path d="M7 11l4.5-8a2.2 2.2 0 0 1 3 2.8L13 10h5.6a2 2 0 0 1 2 2.5l-1.8 6.5a2 2 0 0 1-2 1.5H7"/></svg>No</button>' +
    '</div></div>';
  h += '<div class="pager">' +
    (prev ? '<div class="pg" data-go="' + prev.slug + '"><div class="pg-lbl">Previous</div><div class="pg-ttl">' + prev.title + '</div></div>' : '<div class="pg empty"></div>') +
    (next ? '<div class="pg next" data-go="' + next.slug + '"><div class="pg-lbl">Next</div><div class="pg-ttl">' + next.title + '</div></div>' : '<div class="pg empty"></div>') +
    '</div></article>';
  return h;
}

/* ---------------- module ---------------- */
export function renderModule(mod){
  const items = ARTICLES.filter(a => a.module === mod);
  return '<article><div class="crumb"><a data-go="home">Knowledge Base</a><span class="sep">/</span><span class="cur">' + mod + '</span></div>' +
    '<h1>' + mod + '</h1><div class="meta"><span class="pill">Admin</span><span>' +
    items.filter(a => !a.stub).length + ' of ' + items.length + ' written</span></div>' +
    items.map(a => a.stub
      ? '<div class="sr" style="opacity:.62;cursor:default"><div class="sr-t" style="color:var(--muted)">' + REF(a) + a.title + '</div>' +
        '<div class="sr-m">Not written yet</div></div>'
      : '<div class="sr" data-go="' + a.slug + '"><div class="sr-t">' + REF(a) + a.title + '</div>' +
        '<div class="sr-m">' + a.module + '</div><div class="sr-x">' + strip(a.answer).slice(0, 165) + '…</div></div>'
    ).join('') + '</article>';
}

/* ---------------- search results ---------------- */
function snippetFor(a, stems){
  const full = strip(a.answer) + ' ' + strip(a.body);
  const m = full.match(termRe(stems, 'i'));
  const at = m ? full.indexOf(m[0]) : -1;
  if (at < 0) return strip(a.answer).slice(0, 180) + '…';
  const start = Math.max(0, at - 75);
  return (start ? '…' : '') + full.slice(start, at + 145) + '…';
}

/* Returns the markup and the stems to highlight, so the component can
   mark the hits once the HTML is in the DOM. */
export function renderSearch(q){
  const res = matches(q);
  const stems = searchTerms(q, res.slice(0, 8).map(r => r.a.title + ' . ' + strip(r.a.answer)).join(' . '));

  if (!res.length){
    return { stems, html:
      '<article><div class="empty-search"><h3>No results for &ldquo;' + esc(q) + '&rdquo;</h3>' +
      '<p>Nothing in the knowledge base covers that yet. Try a different word, or browse the modules from the sidebar.</p>' +
      '<div class="logged"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 8v4l3 2"/><circle cx="12" cy="12" r="9"/></svg>' +
      'Logged as an unanswered search — this is how the next articles get chosen.</div></div></article>' };
  }

  /* Nothing covered the whole question well — say so rather than
     presenting a weak guess as a confident answer. */
  const weak = res[0].coverage < 0.5;
  return { stems, html:
    '<article><h2 class="sr-h" style="margin-top:0">' +
    (weak ? 'Closest matches for' : 'Results for') + ' &ldquo;' + esc(q) + '&rdquo;</h2>' +
    '<div class="sr-sub">' + (weak
      ? 'No article covers that exactly. These look closest — if none of them help, the search gets logged so it can be written.'
      : res.length + ' article' + (res.length > 1 ? 's' : '')) + '</div>' +
    res.slice(0, 8).map(r => '<div class="sr" data-go="' + r.a.slug + '"><div class="sr-t">' + REF(r.a) + r.a.title + '</div>' +
      '<div class="sr-m">' + r.a.module + '</div><div class="sr-x">' +
      snippetFor(r.a, stems) + '</div></div>').join('') + '</article>' };
}

/* ---------------- on-this-page ---------------- */
/* Given the rendered column, number its h2s and return TOC markup, or ''
   when there are too few sections to be worth it. Mutates the headings'
   ids — that is the point. */
export function buildToc(root){
  const heads = [...root.querySelectorAll('article h2')];
  if (heads.length < 2) return '';
  heads.forEach((h, i) => { h.id = 'h' + i; });
  return '<aside class="toc"><div class="toc-inner"><div class="toc-h">On this page</div>' +
    heads.map((h, i) => '<a class="toclink" data-h="h' + i + '" tabindex="0" role="link">' + esc(h.textContent) + '</a>').join('') +
    '</div></aside>';
}

/* Highlight matches in already-rendered HTML, walking text nodes only so
   tags and attributes are never touched. */
export function highlight(root, stems){
  if (!root || !stems || !stems.length) return;
  const re = termRe(stems, 'ig');
  const walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n){
      re.lastIndex = 0;
      if (!re.test(n.nodeValue)) return NodeFilter.FILTER_REJECT;
      const p = n.parentElement;
      if (!p || p.closest('mark, script, style, .draftbar')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  const targets = [];
  while (walk.nextNode()) targets.push(walk.currentNode);
  targets.forEach(n => {
    const frag = document.createDocumentFragment();
    let last = 0, m;
    re.lastIndex = 0;
    while ((m = re.exec(n.nodeValue)) !== null){
      if (m.index > last) frag.appendChild(document.createTextNode(n.nodeValue.slice(last, m.index)));
      const mark = document.createElement('mark');
      mark.textContent = m[0];
      frag.appendChild(mark);
      last = m.index + m[0].length;
      if (m[0].length === 0) re.lastIndex++;
    }
    if (last < n.nodeValue.length) frag.appendChild(document.createTextNode(n.nodeValue.slice(last)));
    n.parentNode.replaceChild(frag, n);
  });
}
